// Copyright (c) 2020-2021, 2024 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import ReactCrop, { Crop, ReactCropProps } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { action, makeObservable, observable, runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react';
import isEqual from 'lodash/isEqual';
import { withStyles } from "tss-react/mui";
import { Dialog, DialogActions, DialogContent } from '@mui/material';
import Rotate90DegreesCcwIcon from '@mui/icons-material/Rotate90DegreesCcw';
import { getBase64FromFile } from '@shared/utils/common';
import Flex from '@shared/components/Flex';
import Button from '@shared/components/Button';
import { showConfirmation } from '@shared/components/Confirmation';
import FileUpload, { FileUploadProps } from '@shared/components/FileUpload';
import { showNotification, NotificationType } from '@shared/components/Notification';
import DialogTitle from '@shared/components/DialogTitle';

type ImageCrop = Required<Crop>;

// type Src = ReactCropProps['src'];

export interface CropperProps {
  className?: string;
  src: string;
  uploadProps: Omit<FileUploadProps, 'onUpload' | 'classes'>;
  onApply: (croppedImage: string) => any;
  close: () => any;
}

const DEFAULT_CONFIG: ImageCrop = {
  width: 100,
  height: 100,
  // aspect: 0,
  x: 0,
  y: 0,
  unit: 'px'
};

@observer
class Cropper extends React.Component<CropperProps> {
  private imageRef: HTMLImageElement;
  private rotationAngle = -90;
  @observable src: string = this.props.src;
  @observable config = DEFAULT_CONFIG;

  constructor(props: CropperProps) {
    super(props);
    makeObservable(this);
  }

  private get hasChanges() {
    const currentConfig = toJS(this.config);

    return !isEqual(currentConfig, DEFAULT_CONFIG) || !isEqual(this.src, this.props.src);
  }

  private get scaleX() {
    return this.imageRef.naturalWidth / this.imageRef.width;
  }

  private get scaleY() {
    return this.imageRef.naturalHeight / this.imageRef.height;
  }

  private onCrop = () => {
    const croppedImage = this.getCroppedImg();

    // TODO use better way
    if (croppedImage.length < 10) {
      showNotification('Failed to crop', NotificationType.error);

      return;
    }

    const { onApply, close } = this.props;

    onApply(croppedImage);
    close();
  }

  private handleImageLoaded = (image) => {
    this.imageRef = image.currentTarget;
  }

  @action handleCropChange = (config: ImageCrop) => {
    const width = config.width > this.imageRef.width ? this.imageRef.width : config.width;
    const height = config.height > this.imageRef.height ? this.imageRef.height : config.height;

    this.config = {
      ...config,
      width,
      height
    };
  }

  private rotate = () => {
    const canvas = document.createElement('canvas');
    const { naturalWidth, naturalHeight } = this.imageRef;

    // In order to rotate the image on 90 degrees, we have to replace image width and height
    canvas.width = naturalHeight;
    canvas.height = naturalWidth;

    const canvasCtx = canvas.getContext('2d');

    canvasCtx?.translate(canvas.width / 2, canvas.height / 2);
    canvasCtx?.rotate(this.rotationAngle * Math.PI / 180);

    canvasCtx?.drawImage(
      this.imageRef,
      -(naturalWidth / 2),
      -(naturalHeight / 2),
      naturalWidth,
      naturalHeight,
    );

    this.src = canvas.toDataURL();
  }

  private getCroppedImg(): string {
    const canvas = document.createElement('canvas');

    canvas.width = this.config.width;
    canvas.height = this.config.height;

    const canvasCtx = canvas.getContext('2d');

    canvasCtx?.drawImage(
      this.imageRef,
      this.config.x * this.scaleX,
      this.config.y * this.scaleY,
      this.config.width * this.scaleX,
      this.config.height * this.scaleY,
      0,
      0,
      this.config.width,
      this.config.height
    );

    return canvas.toDataURL();
  }

  private close = () => {
    const { close } = this.props;

    if (this.hasChanges) {
      const leaveCropper = (confirmed = true) => {
        if (!confirmed) {
          return;
        }

        close();
      };

      showConfirmation(
        { title: 'Unsaved changes', question: 'Are you sure you want to leave? The image won\'t be saved.' },
        leaveCropper
      );
    } else {
      close();
    }
  }

  private handleUploadAnother = async (file: File) => {
    runInAction(() => {
      this.config = DEFAULT_CONFIG;
    });
    this.src = await getBase64FromFile(file);
  }

  render() {
    const { uploadProps } = this.props;
    const classes = withStyles.getClasses(this.props);

    return (
      <Dialog open classes={{ paper: classes.root }}>
        <DialogTitle
          heading="Edit construction photo"
          classes={{
            heading: classes.heading,
          }}
          onClose={this.close}
        />
        <DialogContent className={classes.content}>
          <Flex
            justifyContent="space-between"
            wrap="nowrap"
          >
            <ReactCrop
              ruleOfThirds
              crop={this.config}
              onChange={this.handleCropChange}
              className={classes.cropRoot}
            >
              <img src={this.src} onLoad={this.handleImageLoaded}/>
            </ReactCrop>
            <div className={classes.panel}>
              <Button
                disableRipple
                color="primary"
                variant="outlined"
                size="small"
                className={classes.uploadAnotherBtn}
              >
                <FileUpload
                  {...uploadProps}
                  placeholder="Upload another"
                  onUpload={this.handleUploadAnother}
                  classes={{
                    root: classes.fileUploadRoot,
                    placeholder: classes.fileUploadPlaceholder
                  }}
                />
              </Button>
              <Button
                disableRipple
                text={(
                  <Flex alignItems="center">
                    <Rotate90DegreesCcwIcon className={classes.rotationIcon} />
                    <span className={classes.rotateText}>Rotate</span>
                  </Flex>
                )}
                color="primary"
                variant="outlined"
                size="small"
                className={classes.rotateBtn}
                onClick={this.rotate}
              />
            </div>
          </Flex>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button
            text="Apply"
            color="secondary"
            variant="contained"
            size="small"
            onClick={this.onCrop}
          />
          <Button
            text="Cancel"
            color="primary"
            size="small"
            onClick={this.close}
          />
        </DialogActions>
      </Dialog>
    );
  }
}

const CropperStyled = withStyles(
  Cropper, 
  (theme, props) => ({
    "root": {
      width: 555,
      maxWidth: 'none',
    },
    "content": {
      padding: theme.spacing(6),
    },
    "actions": {
      padding: theme.spacing(4, 6),
      justifyContent: 'flex-start',
      backgroundColor: theme.palette.colors.greyLight5,
    },
    "heading": {
      fontSize: 20,
    },
    "cropRoot": {
      '& img': {
        objectFit: 'contain',
        maxWidth: 340,
        maxHeight: 340,
        minWidth: 100,
        minHeight: 100,
      }
    },
    "panel": {
      paddingLeft: theme.spacing(4),
    },
    "rotationIcon": {
      fontSize: 16,
      marginRight: theme.spacing(),
    },
    "rotateBtn": {
      marginTop: theme.spacing(3),
    },
    "rotateText": {
      fontWeight: 500,
    },
    "uploadAnotherBtn": {
      position: 'relative',
      width: '100%',
      overflow: 'hidden',
    },
    "fileUploadRoot": {
      position: 'absolute',
      width: '100%',
      height: '100%',
      backgroundColor: theme.palette.colors.white,
      border: 'none',
    },
    "fileUploadPlaceholder": {
      // color: theme.palette.colors.black,
      fontWeight: 500,
      fontSize: 12,
    }
  })
);

export default CropperStyled;